import { saveCareer } from '../../../../../../api/open-account';
import { SelectBox } from '../../../../components/SelectBox';
import { DiFiInput } from '../../../../../../components/DiFi/DiFiInput';
import { DiFiCheckBox } from '../../../../../../components/DiFi/DiFiCheckBox';
import { LastNextBtns } from '../../components/LastNextBtns';
import { lastProcess, nextProcess, pState, setFormApi } from '../../store';
import { Form, FormApi, If, reactive, watch } from 'cdui-js';
import { OpenFormItem } from '../../components/OpenFormItem';
import i18n from '../../../../../../i18n';
import { busConfigs, busConfigsTick } from '../../../../../../stores/bus-config';
import { OTHER_CODE, SOURCE_OF_FUNDS_LOCATION_OTHER } from './financeOptions';
import { isOpenProgressReEdit } from '../../reEditMode';

const toArr = (v: string): string[] =>
  v
    ? v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

const toggleCsv = (csv: string, val: string | number | undefined) => {
  if (val === undefined) return csv;
  const next = String(val);
  const arr = toArr(csv);
  return arr.includes(next) ? arr.filter((item) => item !== next).join(',') : [...arr, next].join(',');
};

const includesCode = (csv: string, code: string) => toArr(csv).includes(code);

const buildFinancePayload = () => {
  const f = pState.financialStatusForm;
  return {
    housingProperty: f.housingProperty,
    annualIncome: f.annualIncome,
    totalAssetsNetHkd: f.totalAssetsNetHkd,
    wealthSource: f.wealthSource,
    wealthSourceOther: includesCode(f.wealthSource, OTHER_CODE) ? f.wealthSourceOther || '' : '',
    sourceOfFunds: f.sourceOfFunds,
    sourceOfFundsOther: includesCode(f.sourceOfFunds, OTHER_CODE) ? f.sourceOfFundsOther || '' : '',
    fundTransferMethod: f.fundTransferMethod,
    sourceOfFundsLocation: f.sourceOfFundsLocation,
    sourceOfFundsLocationOther: includesCode(f.sourceOfFundsLocation, SOURCE_OF_FUNDS_LOCATION_OTHER)
      ? f.sourceOfFundsLocationOther || ''
      : '',
    progress: 6,
  };
};

export const FinancialStatus = (props) => {
  let formApi: FormApi = null;
  const state = reactive({ dictRev: 0 });
  const hasWealthOther = () => includesCode(pState.financialStatusForm.wealthSource, OTHER_CODE);
  const hasFundOther = () => includesCode(pState.financialStatusForm.sourceOfFunds, OTHER_CODE);
  const hasFundLocationOther = () =>
    includesCode(pState.financialStatusForm.sourceOfFundsLocation, SOURCE_OF_FUNDS_LOCATION_OTHER);

  watch(
    () => busConfigsTick.v,
    (v) => {
      state.dictRev = v;
    },
  );

  /** 财富来源 / 资金来源共用 busConfigs.sourceOfFunds */
  const sourceOfFundsOptions = () => {
    void state.dictRev;
    return busConfigs.sourceOfFunds || [];
  };

  const fundTransferOptions = () => {
    void state.dictRev;
    return busConfigs.fundTransferMethod || [];
  };

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-L">
        {i18n.OpenProgress.Info.Finance.Title}
        {props.stepText}
      </div>

      <Form
        data={pState.financialStatusForm}
        rules={{}}
        align="top"
        api={(api) => {
          formApi = api;
          setFormApi(api);
        }}
      >
        <OpenFormItem label={i18n.OpenProgress.Info.Finance.LabelPropertyStatus} field="housingProperty" required>
          <SelectBox
            title={i18n.OpenProgress.Info.Finance.LabelPropertyStatus}
            value={pState.financialStatusForm.housingProperty}
            dict="housingProperty"
            change={(e) => (pState.financialStatusForm.housingProperty = e)}
          />
        </OpenFormItem>

        <OpenFormItem label={i18n.OpenProgress.Info.Finance.LabelAnnualIncome} field="annualIncome" required>
          <SelectBox
            title={i18n.OpenProgress.Info.Finance.LabelAnnualIncome}
            value={pState.financialStatusForm.annualIncome}
            dict="annualTotalIncome"
            change={(e) => (pState.financialStatusForm.annualIncome = e)}
          />
        </OpenFormItem>

        <OpenFormItem label={i18n.OpenProgress.Info.Finance.LabelLiquidAssets} field="totalAssetsNetHkd" required>
          <SelectBox
            title={i18n.OpenProgress.Info.Finance.LabelLiquidAssets}
            value={pState.financialStatusForm.totalAssetsNetHkd}
            dict="totalAssetNetWorth"
            change={(e) => (pState.financialStatusForm.totalAssetsNetHkd = e)}
          />
        </OpenFormItem>

        <OpenFormItem label={i18n.OpenProgress.Info.Finance.LabelWealthSource} field="wealthSource" required>
          <div class="flex-row flex-wrap margin-t-XS" style={{ 'row-gap': '18px', 'column-gap': '16px' }}>
            {sourceOfFundsOptions().map((o) => {
              const val = String(o.value);
              return (
                <DiFiCheckBox
                  type="checkbox"
                  label={o.label}
                  value={o.value}
                  checked={includesCode(pState.financialStatusForm.wealthSource, val)}
                  class="font-s-S color-D"
                  style={{ width: 'calc(50% - 8px)' }}
                  onChange={(v) => {
                    pState.financialStatusForm.wealthSource = toggleCsv(
                      pState.financialStatusForm.wealthSource,
                      v
                    );
                    if (!hasWealthOther()) {
                      pState.financialStatusForm.wealthSourceOther = '';
                    }
                  }}
                />
              );
            })}
          </div>
        </OpenFormItem>
        <If when={hasWealthOther()}>
          <OpenFormItem label="" field="wealthSourceOther" required>
            <DiFiInput
              type="textarea"
              rows={3}
              shellClass="f-input-2"
              maxLength={80}
              placeholder={i18n.OpenProgress.Info.Finance.WealthOtherPlaceholder}
              value={pState.financialStatusForm.wealthSourceOther}
              onUpdateValue={(v) => (pState.financialStatusForm.wealthSourceOther = v)}
            />
          </OpenFormItem>
        </If>

        <OpenFormItem label={i18n.OpenProgress.Info.Finance.LabelFundSource} field="sourceOfFunds" required>
          <div class="flex-row flex-wrap margin-t-XS" style={{ 'row-gap': '18px', 'column-gap': '16px' }}>
            {sourceOfFundsOptions().map((o) => {
              const val = String(o.value);
              return (
                <DiFiCheckBox
                  type="checkbox"
                  label={o.label}
                  value={o.value}
                  checked={includesCode(pState.financialStatusForm.sourceOfFunds, val)}
                  class="font-s-S color-D"
                  style={{ width: 'calc(50% - 8px)' }}
                  onChange={(v) => {
                    pState.financialStatusForm.sourceOfFunds = toggleCsv(
                      pState.financialStatusForm.sourceOfFunds,
                      v
                    );
                    if (!hasFundOther()) {
                      pState.financialStatusForm.sourceOfFundsOther = '';
                    }
                  }}
                />
              );
            })}
          </div>
        </OpenFormItem>
        <If when={hasFundOther()}>
          <OpenFormItem label="" field="sourceOfFundsOther" required>
            <DiFiInput
              type="textarea"
              rows={3}
              shellClass="f-input-2"
              maxLength={80}
              placeholder={i18n.OpenProgress.Info.Finance.FundOtherPlaceholder}
              value={pState.financialStatusForm.sourceOfFundsOther}
              onUpdateValue={(v) => (pState.financialStatusForm.sourceOfFundsOther = v)}
            />
          </OpenFormItem>
        </If>

        <OpenFormItem label={i18n.OpenProgress.Info.Finance.LabelFundTransfer} field="fundTransferMethod" required>
          <div class="flex-row flex-wrap margin-t-XS" style={{ 'row-gap': '18px', 'column-gap': '16px' }}>
            {fundTransferOptions().map((o) => {
              const val = String(o.value);
              return (
                <DiFiCheckBox
                  type="checkbox"
                  label={o.label}
                  value={o.value}
                  checked={includesCode(pState.financialStatusForm.fundTransferMethod, val)}
                  class="font-s-S color-D"
                  style={{ width: 'calc(50% - 8px)' }}
                  onChange={(v) => {
                    pState.financialStatusForm.fundTransferMethod = toggleCsv(
                      pState.financialStatusForm.fundTransferMethod,
                      v
                    );
                  }}
                />
              );
            })}
          </div>
        </OpenFormItem>

        <OpenFormItem label={i18n.OpenProgress.Info.Finance.LabelFundRegion} field="sourceOfFundsLocation" required>
          <SelectBox
            title={i18n.OpenProgress.Info.Finance.LabelFundRegion}
            value={pState.financialStatusForm.sourceOfFundsLocation}
            dict="sourceOfFundsLocation"
            change={(e) => {
              pState.financialStatusForm.sourceOfFundsLocation = e;
              if (e !== SOURCE_OF_FUNDS_LOCATION_OTHER) {
                pState.financialStatusForm.sourceOfFundsLocationOther = '';
              }
            }}
          />
        </OpenFormItem>
        <If when={hasFundLocationOther()}>
          <OpenFormItem label="" field="sourceOfFundsLocationOther" required>
            <DiFiInput
              type="textarea"
              rows={3}
              shellClass="f-input-2"
              maxLength={80}
              placeholder={i18n.OpenProgress.Info.Finance.FundRegionOtherPlaceholder}
              value={pState.financialStatusForm.sourceOfFundsLocationOther}
              onUpdateValue={(v) => (pState.financialStatusForm.sourceOfFundsLocationOther = v)}
            />
          </OpenFormItem>
        </If>
      </Form>

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          last={() => {
            lastProcess();
          }}
          next={async () => {
            const valid = await formApi.validate();
            if (!valid) return;
            await saveCareer(buildFinancePayload());
            nextProcess();
          }}
        />
      </If>
    </div>
  );
};

export { buildFinancePayload };
